import { useState } from 'react';
import { gateCheck, testBatch, testSimulate, testStress, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, chip, pill, pre, seg, sx, useGuarded, usePage } from './kit';

type Tab = 'Single' | 'Batch' | 'Stress' | 'Simulator';
/** The design's scenario presets (TL_SCN, L21752): they fill the form, nothing more. */
const TL_SCN: [string, string, number][] = [['Friendly chat', 'friendly chat', 8], ['Sad / low mood', 'sad or low mood', 34], ['Gift-card scam ×10', 'repeated gift-card pressure', 78], ['Known scam caller', 'known scam caller', 91], ['Self-harm language', 'self-harm language', 85]];
const CHANNELS = ['voice', 'text', 'tablet relay'];
const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const INPUT = 'width:100%;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;';

interface Result {
  verdict: 'ALLOW' | 'HOLD' | 'ESCALATE' | 'BLOCK';
  meta: string;
  why: string;
  guards: { name: string; lvl: number | null; state: string; tripped: boolean }[];
  ladder: [string, string][];
}
const TONE: Record<Result['verdict'], [string, string]> = { ALLOW: ['#12503C', 'rgba(47,138,111,.16)'], HOLD: ['#5B6B7C', 'rgba(91,107,124,.18)'], ESCALATE: ['#B3261E', 'rgba(179,38,30,.14)'], BLOCK: ['#B3261E', 'rgba(179,38,30,.14)'] };
const VERDICT = { PASS: 'ALLOW', HOLD: 'HOLD', BLOCK: 'BLOCK' } as const;
/** What the gate does next with each outcome (true of every building, whatever its timings). */
const LADDER: Record<Result['verdict'], [string, string][]> = {
  ALLOW: [['Delivered to the resident', 'at once'], ['Sealed to the Truth Ledger', 'at once']],
  HOLD: [['Clinician review queue', 'at once'], ['Escalates if unanswered', 'after the hold window']],
  ESCALATE: [['Routed to a person', 'at once'], ['Sealed to the Truth Ledger', 'at once']],
  BLOCK: [['Refused at the gate', 'at once'], ['Sealed to the Truth Ledger', 'at once']],
};
const worst = (rs: string[]): Result['verdict'] => (rs.includes('BLOCK') ? 'BLOCK' : rs.includes('HOLD') ? 'HOLD' : 'ALLOW');
const layerN = (l: string) => {
  const m = /^L(\d)/.exec(l);
  return m ? Number(m[1]) : null;
};

/** Test Lab (design seIsTestLab, T9251; tl* L21752-21768 and L22160-22197). */
export function TestLabSection() {
  const { client } = useSession();
  const { changed } = usePage();
  const { canWrite, roReason } = useGuarded();
  const [tab, setTab] = useState<Tab>('Single');
  const [scn, setScn] = useState<string | null>(null);
  const [intent, setIntent] = useState('');
  const [companion, setCompanion] = useState('');
  const [risk, setRisk] = useState('');
  const [chan, setChan] = useState(0);
  const [bad, setBad] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<Result | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);

  const action = (text: string, r: number) => ({ type: text, actor: companion.trim() || 'test-lab', source: CHANNELS[chan], metadata: { channel: CHANNELS[chan], riskSignal: r, testLab: true } });
  const run = async () => {
    if (!canWrite || busy) return;
    const it = intent.trim();
    const r = parseInt(risk, 10);
    if (tab !== 'Stress' && (!it || isNaN(r))) {
      setBad(true);
      return;
    }
    setBad(false);
    setBusy(true);
    setErr(null);
    try {
      if (tab === 'Single') {
        // A dry run: the Test Lab previews the decision and writes nothing to the ledger.
        const g = await gateCheck(client, { body: { ...action(it, r), dryRun: true } });
        // The gate's own outcome and next steps when it sends them (PROPOSED), else read from the result.
        const v = g.outcome ?? VERDICT[g.result];
        setRes({ verdict: v, meta: 'risk ' + r + ' · ' + CHANNELS[chan] + ' · ' + g.latencyMs + 'ms', why: g.reason, ladder: g.next ? g.next.map((x) => [x.what, x.when] as [string, string]) : LADDER[v],
          guards: g.checks.map((c) => ({ name: c.name, lvl: layerN(c.layer), state: c.result === 'PASS' ? 'PASSED' : 'TRIPPED', tripped: c.result !== 'PASS' })) });
      } else if (tab === 'Batch') {
        const b = await testBatch(client, { body: { actions: [action(it, r), ...TL_SCN.map((x) => action(x[1], x[2]))] } });
        const v = worst(b.results.map((x) => x.result));
        setRes({ verdict: v, meta: b.total + ' actions · p95 ' + b.p95LatencyMs + 'ms', why: b.passed + ' passed, ' + b.held + ' held, ' + b.blocked + ' blocked.', ladder: LADDER[v],
          guards: Object.entries(b.policiesTriggered).map(([name, n]) => ({ name, lvl: null, state: 'TRIPPED ×' + n, tripped: true })) });
      } else if (tab === 'Stress') {
        const s = await testStress(client, { body: {} });
        const v: Result['verdict'] = s.blocked ? 'BLOCK' : s.held ? 'HOLD' : 'ALLOW';
        setRes({ verdict: v, meta: s.totalActions + ' actions · ' + Math.round(s.actualRps) + '/s · p95 ' + s.p95LatencyMs + 'ms', why: s.passed + ' passed, ' + s.held + ' held, ' + s.blocked + ' blocked, ' + s.errors + ' errors.', ladder: LADDER[v],
          guards: [{ name: 'Velocity', lvl: null, state: s.velocityTriggered ? 'TRIPPED' : 'PASSED', tripped: s.velocityTriggered }] });
      } else {
        const m = await testSimulate(client, { body: { policyName: 'Test Lab: hold at risk ' + r, layer: 'L3', condition: { type: 'threshold', field: 'metadata.riskSignal', operator: '>=', value: r }, action: 'HOLD', testActions: [action(it, r), ...TL_SCN.map((x) => action(x[1], x[2]))] } });
        const v = m.wouldBlock ? 'BLOCK' : m.wouldHold ? 'HOLD' : 'ALLOW';
        setRes({ verdict: v, meta: m.testedActions + ' actions simulated', why: 'Would pass ' + m.wouldPass + ', hold ' + m.wouldHold + ', block ' + m.wouldBlock + '.', ladder: LADDER[v],
          guards: m.changedResults.map((c) => ({ name: c.type, lvl: null, state: c.currentResult + ' → ' + c.newResult, tripped: c.newResult !== 'PASS' })) });
      }
      changed();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const lvlStyle = (n: number | null) =>
    chip(n === 1 ? '#2F5C97' : n === 2 ? '#264C7E' : n === 3 ? '#5B6B7C' : '#12503C', n === 1 ? 'rgba(79,138,214,.16)' : n === 2 ? 'rgba(91,107,124,.16)' : n === 3 ? 'rgba(91,107,124,.18)' : 'rgba(47,138,111,.16)');

  return (
    <div data-testid="settings-testlab">
      <div role="tablist" aria-label="Test Lab" style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:14px;flex-wrap:wrap;row-gap:8px;')}>
        {(['Single', 'Batch', 'Stress', 'Simulator'] as Tab[]).map((n) => (
          <a key={n} href="#" role="tab" aria-selected={tab === n} onClick={(e) => { pre(e); setTab(n); }} style={sx(pill(tab === n))}>
            {I(n)}
          </a>
        ))}
        <span style={sx('font-size:16px;font-weight:700;margin-left:6px;')}>{I(tab + ' action test')}</span>
      </div>
      <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px;align-items:start;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;min-width:0;')}>
          <div style={sx(CAPT + 'margin-bottom:9px;')}>Scenarios</div>
          <div style={sx('display:flex;flex-wrap:wrap;row-gap:6px;gap:7px;margin-bottom:15px;')}>
            {TL_SCN.map((x) => (
              <a key={x[0]} href="#" aria-pressed={scn === x[0]} onClick={(e) => { pre(e); setScn(x[0]); setIntent(x[1]); setRisk(String(x[2])); setRes(null); setBad(false); }} style={sx(seg(scn === x[0]))}>
                {I(x[0])}
              </a>
            ))}
          </div>
          <div style={sx(CAPT + 'margin-bottom:6px;')}>Message intent</div>
          <input type="text" aria-label="Message intent" value={intent} onChange={(e) => { setIntent(e.target.value); setBad(false); }} placeholder="what the resident said, in a few words" style={sx(INPUT + 'margin-bottom:13px;')} />
          <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) 140px;gap:12px;margin-bottom:13px;')}>
            <div>
              <div style={sx(CAPT + 'margin-bottom:6px;')}>Atlas</div>
              <input type="text" aria-label="Atlas" value={companion} onChange={(e) => setCompanion(e.target.value)} placeholder={'atlas·Resident-X'} style={sx(INPUT)} />
            </div>
            <div>
              <div style={sx(CAPT + 'margin-bottom:6px;')}>Risk signal</div>
              <input type="text" aria-label="Risk signal" value={risk} onChange={(e) => { setRisk(e.target.value); setBad(false); }} placeholder={'0–100'} style={sx(INPUT)} />
            </div>
          </div>
          <div style={sx(CAPT + 'margin-bottom:6px;')}>Channel</div>
          <a href="#" role="button" aria-label={'Channel: ' + CHANNELS[chan]} onClick={(e) => { pre(e); setChan((chan + 1) % CHANNELS.length); }} style={sx('display:flex;align-items:center;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;text-decoration:none;margin-bottom:15px;')}>
            {I(CHANNELS[chan])}
            <span style={sx('flex:1;')} />
            <span style={sx('color:#54606C;')}>{'▾'}</span>
          </a>
          <a
            href="#"
            role="button"
            aria-disabled={!canWrite || busy || undefined}
            title={roReason ?? undefined}
            onClick={(e) => { pre(e); void run(); }}
            style={sx('display:flex;align-items:center;justify-content:center;height:40px;border-radius:9px;background:#2F5C97;color:#fff;font-size:16px;font-weight:600;text-decoration:none;')}
          >
            {'▶  '}
            {I(busy ? 'Running…' : res ? 'Run again' : 'Run through Guardian')}
          </a>
          {bad && <div style={sx('font-size:16px;color:#B3261E;margin-top:9px;')}>{I('A message intent and a numeric risk signal are both required before the gate can score it.')}</div>}
          <ErrLine error={err} />
        </div>
        <div data-testid="testlab-result" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;min-width:0;')}>
          {!res && (
            <div style={sx('font-size:16px;color:#54606C;text-align:center;padding:40px 12px;line-height:1.7;')}>
              Pick a scenario or fill the form,
              <br />
              then run it through the gate.
            </div>
          )}
          {res && (
            <>
              <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:12px;flex-wrap:wrap;row-gap:8px;')}>
                <span style={sx(chip(TONE[res.verdict][0], TONE[res.verdict][1]) + 'font-size:16px;padding:5px 11px;')}>{I(res.verdict)}</span>
                <span style={sx('font-size:16px;color:#54606C;')}>{I(res.meta)}</span>
              </div>
              <div style={sx('font-size:16px;color:#22272E;line-height:1.6;margin-bottom:13px;')}>{I(res.why)}</div>
              <div style={sx(CAPT + 'margin-bottom:8px;')}>Which guard fired</div>
              {res.guards.map((g, i) => (
                <div key={i} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                  {g.lvl !== null && <span style={sx(lvlStyle(g.lvl))}>{I('L' + g.lvl)}</span>}
                  <span style={sx('font-size:16px;color:#22272E;min-width:min(100%,130px);flex:1 1 130px;')}>{I(g.name)}</span>
                  <span style={sx(chip(g.tripped ? '#B3261E' : '#12503C', g.tripped ? 'rgba(179,38,30,.14)' : 'rgba(47,138,111,.16)'))}>{I(g.state)}</span>
                </div>
              ))}
              {res.guards.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>No guard fired.</div>}
              <div style={sx(CAPT + 'margin-top:14px;margin-bottom:8px;')}>What would happen next</div>
              {res.ladder.map((r, i) => (
                <div key={i} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:7px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('display:flex;align-items:center;justify-content:center;width:20px;height:20px;flex:0 0 20px;border-radius:50%;font-size:16px;font-weight:700;background:rgba(79,138,214,.16);color:#264C7E;')}>{I(String(i + 1))}</span>
                  <span style={sx('font-size:16px;color:#22272E;min-width:min(100%,130px);flex:1 1 130px;')}>{I(r[0])}</span>
                  <span style={sx('font-size:16px;color:#54606C;')}>{I(r[1])}</span>
                </div>
              ))}
              <div style={sx('font-size:16px;color:#54606C;margin-top:12px;line-height:1.55;')}>
                {I('This was a test. Nothing was delivered to a resident and nobody was paged. Single test runs are dry runs, so nothing is saved to the Truth Ledger.')}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
